import { skipToMessage } from './MailView.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda */
import { observer } from 'mobx-react';
import { HotkeysMap } from 'owa-hotkeys-map';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getCommandCategories, getCommands } from 'owa-mail-hotkeys';
import { ListPane } from 'owa-mail-list-pane-view';
import { listViewStore, shouldSuppressServerMarkReadOnReplyOrForward } from 'owa-mail-list-store';
import setListViewDimension from 'owa-mail-module-actions/lib/actions/setListViewDimension';
import ReadingPaneContainer from 'owa-mail-reading-pane-container/lib/components/ReadingPaneContainer';
import store from 'owa-mail-store/lib/store/Store';
import { NotificationBarHost } from 'owa-notification-bar';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { lazyTriggerResizeEvent } from 'owa-resize-event';
import { ResizeHandle, ResizeHandleDirection } from 'owa-resize-handle';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { getStore as getTabStore, TabType } from 'owa-tab-store';
import { TabViewState } from 'owa-tab-store';
import { TabBar } from 'owa-tab-view';
import * as React from 'react';
import isInboxPlusEnabled from 'owa-mail-inbox-plus-store/lib/selectors/isInboxPlusEnabled';
import getMailListHeader from 'owa-mail-list-pane-view/lib/components/getMailListHeader';
import { globalStyles } from 'owa-global-styles';
import getStyleSelectorAsPerUserSettings from 'owa-mail-list-view/lib/utils/getStyleSelectorAsPerUserSettings';
import { addSkipLinkRegion } from 'owa-skip-link/lib/utils/addSkipLinkRegion';

const classNames = require('classnames');
const styles = require('./MailModule.scss');

export interface Props {
    activeTab: TabViewState;
    isDumpsterOrDumpsterSearchTable: boolean;
    listViewDimensions: { listViewWidth: number; listViewHeight: number };
    readingPanePosition: number;
}

@observer
export default class MailView extends React.Component<Props, {}> {
    render() {
        const { activeTab, isDumpsterOrDumpsterSearchTable, readingPanePosition } = this.props;
        const { listViewWidth, listViewHeight } = this.props.listViewDimensions;
        const tableViewId = listViewStore.selectedTableViewId;
        const tableView = listViewStore.tableViews.get(tableViewId);
        const styleSelectorAsPerUserSettings = getStyleSelectorAsPerUserSettings(tableViewId);

        const isReadingPanePositionRight = readingPanePosition === ReadingPanePosition.Right;
        const isReadingPanePositionBottom = readingPanePosition === ReadingPanePosition.Bottom;

        // Set styles and classNames.
        const mailListStyle: React.CSSProperties = {
            width: isReadingPanePositionRight ? listViewWidth : '',
            height: isReadingPanePositionBottom ? listViewHeight : '',
        };

        let showReadingPane = store.mailModule.showReadingPane;
        let showListPane = store.mailModule.showListPane;

        // Adjust list view & reading pane state for tab view
        let showTabBar = false;

        if (activeTab && !isDumpsterOrDumpsterSearchTable) {
            // Show tab bar when there is a secondary visible tab
            getTabStore().tabs.forEach(tab => {
                showTabBar = showTabBar || (tab.isShown && tab != activeTab);
            });

            if (isReadingPanePositionBottom) {
                // When reading pane is on bottom and current tab is full compose, we need to hide list view
                // to give compose enough space.
                // Here we only check TabType.Compose. There are still 2 cases that compose can be show but
                // we don't check them here, because:
                // 1. current tab is Primary tab and we have primary compose. This happens when user is open
                // draft items folder. In that case we still show list view otherwise user can never see list
                // view of draft folder.
                // 2. inline compose. This should never happen when reading pane is on bottom.
                showListPane = showListPane && activeTab.type != TabType.Compose;
            } else if (!isReadingPanePositionRight) {
                // When reading pane is off, need to adjust reading pane/list view according to current tab type
                // When active tab is primary tab, show reading apne/list view according to the setting in mailStore
                // Otherwise, always show reading pane since reading pane is the container for all other tabs
                showReadingPane =
                    activeTab.type != TabType.Primary || store.mailModule.showReadingPane;
                showListPane = activeTab.type == TabType.Primary && store.mailModule.showListPane;
            }
        }

        const showTabBarInListView =
            showTabBar && showListPane && !showReadingPane && !isDumpsterOrDumpsterSearchTable;
        const showTabBarInReadingPane =
            showTabBar && !showTabBarInListView && !isDumpsterOrDumpsterSearchTable;

        const mailListClasses = classNames(
            isReadingPanePositionRight ? styles.mailList : styles.mailListSLV,
            !showListPane && styles.mailListHide,
            isReadingPanePositionBottom && styles.mailListBottomBorder,
            globalStyles.addFocusOutline
        );

        const subCommandBarPaneClasses = classNames(
            isReadingPanePositionBottom
                ? styles.subCommandBarPaneColumn
                : styles.subCommandBarPaneRow,
            styles.subCommandBarPaneCommon,
            styles.subCommandBarPaneMail
        );

        const showNotificationBar =
            (isFeatureEnabled('tri-notificationBar') || isFeatureEnabled('cmp-delaySend')) &&
            !isReadingPanePositionRight;

        const shouldShowHeader =
            isInboxPlusEnabled() &&
            folderNameToId('inbox') === tableView.tableQuery.folderId &&
            isReadingPanePositionRight &&
            !isReadingPanePositionBottom;

        const isNotesFolder = tableView.tableQuery.folderId === folderNameToId('notes');

        /**
         * KeyboardShortcutsMap requires an array of HotkeyCommands, so convert
         * the object from MailModuleHotKeys to an array.
         */
        const commandsObject = getCommands();
        const hotkeyCommands = [];
        Object.keys(commandsObject).map(key => {
            // Until rp-selectionUI is WW we need to hide focusNextItemPart and focusPrevItemPart from the hotkey commands
            if (
                !(key == 'focusNextItemPart' || key == 'focusPrevItemPart') ||
                isFeatureEnabled('rp-selectionUI')
            ) {
                hotkeyCommands.push(commandsObject[key]);
            }
        });

        return (
            <div className={styles.rightPane}>
                {shouldShowHeader && getMailListHeader(tableViewId, styleSelectorAsPerUserSettings)}
                <div className={subCommandBarPaneClasses}>
                    <ListPane
                        style={mailListStyle}
                        className={mailListClasses}
                        tableViewId={tableViewId}
                        shouldShowHeader={shouldShowHeader}
                        tabBar={showTabBarInListView ? <TabBar /> : null}
                        styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                    />
                    {// Only show resize handle when Reading Pane and List Pane should be shown
                    showReadingPane && showListPane && (
                        <ResizeHandle
                            direction={
                                isReadingPanePositionBottom
                                    ? ResizeHandleDirection.horizontal
                                    : ResizeHandleDirection.vertical
                            }
                            onResize={this.onRightPaneResize}
                        />
                    )}
                    {showReadingPane && (
                        <div className={styles.readingPane}>
                            {addSkipLinkRegion(
                                loc(skipToMessage),
                                'complementary',
                                <ReadingPaneContainer
                                    isDumpsterOrDumpsterSearchTable={
                                        isDumpsterOrDumpsterSearchTable
                                    }
                                    suppressServerMarkReadOnReplyOrForward={shouldSuppressServerMarkReadOnReplyOrForward(
                                        tableView
                                    )}
                                    isNotesFolder={isNotesFolder}
                                />
                            )}
                            {showTabBarInReadingPane && <TabBar />}
                        </div>
                    )}
                    {showNotificationBar && <NotificationBarHost />}
                </div>
                <HotkeysMap
                    commandCategories={getCommandCategories()}
                    goToOptions={() => {
                        lazyMountAndShowFullOptions.importAndExecute('general', 'accessibility');
                    }}
                    hotkeyCommands={hotkeyCommands}
                />
            </div>
        );
    }

    private onRightPaneResize(listViewDimension: number, readingPaneDimension: number) {
        const userConfiguration = getUserConfiguration();
        const readingPanePosition = userConfiguration.UserOptions.GlobalReadingPanePositionReact;
        const isWidth = readingPanePosition == ReadingPanePosition.Right;
        setListViewDimension(listViewDimension, isWidth);
        lazyTriggerResizeEvent.importAndExecute();
    }
}
